'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import VesselDetailsStep from './VesselDetailsStep';
import CategoryChecklistStep from './CategoryChecklistStep';
import ReviewSubmitStep from './ReviewSubmitStep';
import InspectionProgressSidebar from './InspectionProgressSidebar';
import { INSPECTION_CATEGORIES, type InspectionFormData, type CategoryAnswers } from './inspectionData';

const STEPS = [
  { id: 'vessel', label: 'Vessel Details' },
  ...INSPECTION_CATEGORIES.map((c) => ({ id: c.code, label: c.shortLabel })),
  { id: 'review', label: 'Review & Submit' },
];

export default function InspectionApplicationClient() {
  const [currentStep, setCurrentStep] = useState(0);
  const [categoryAnswers, setCategoryAnswers] = useState<Record<string, CategoryAnswers>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<InspectionFormData>({
    defaultValues: {
      vesselName: '',
      imoNumber: '',
      vesselType: 'Bulk Carrier',
      deadweight: '',
      grossTonnage: '',
      flag: '',
      classificationSociety: '',
      portOfRegistry: '',
      deliveryDate: '',
      piClub: '',
      portOfInspection: '',
      countryOfInspection: '',
      embarkationDateTime: '',
      disembarkationDateTime: '',
      inspectorName: '',
      inspectionType: 'Full',
      dateOfInspection: '',
      customerScope: 'Anglo American',
    },
  });

  const totalCategories = INSPECTION_CATEGORIES.length;
  const currentCategoryIndex = currentStep - 1; // step 0 = vessel, steps 1..14 = categories, last = review

  const getCategoryCompletion = (catCode: string) => {
    const cat = INSPECTION_CATEGORIES.find((c) => c.code === catCode);
    if (!cat) return { answered: 0, total: 0 };
    const answers = categoryAnswers[catCode] || {};
    const answered = cat.items.filter((item) => answers[item.ref]?.answer).length;
    return { answered, total: cat.items.length };
  };

  const handleCategoryAnswersUpdate = (catCode: string, answers: CategoryAnswers) => {
    setCategoryAnswers((prev) => ({ ...prev, [catCode]: answers }));
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      const valid = await form.trigger();
      if (!valid) return;
    }
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // TODO: Backend integration — POST /api/inspections with form data + categoryAnswers
    await new Promise((r) => setTimeout(r, 1800));
    setIsSubmitting(false);
    setSubmitted(true);
    toast.success('Inspection submitted successfully', {
      description: `${form.getValues('vesselName') || 'Vessel'} inspection has been saved to the central database.`,
    });
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8 animate-fade-in">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'hsl(145 60% 40% / 0.12)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="hsl(145,60%,30%)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Inspection Submitted</h2>
          <p className="text-muted text-sm max-w-md">
            The inspection for <strong>{form.getValues('vesselName')}</strong> (IMO {form.getValues('imoNumber')}) has been consolidated into the central database.
          </p>
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary" onClick={() => { setSubmitted(false); setCurrentStep(0); form.reset(); setCategoryAnswers({}); }}>
            Start New Inspection
          </button>
          <a href="/inspection-data-board" className="btn-primary">View Data Board</a>
        </div>
      </div>
    );
  }

  const isReviewStep = currentStep === STEPS.length - 1;
  const isCategoryStep = currentStep >= 1 && !isReviewStep;
  const currentCat = isCategoryStep ? INSPECTION_CATEGORIES[currentCategoryIndex] : null;

  return (
    <div className="flex min-h-screen">
      {/* Progress Sidebar */}
      <InspectionProgressSidebar
        currentStep={currentStep}
        steps={STEPS}
        categories={INSPECTION_CATEGORIES}
        getCategoryCompletion={getCategoryCompletion}
        onStepClick={setCurrentStep}
        inspectionType={form.watch('inspectionType')}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="sticky top-0 z-30 bg-white border-b px-6 py-4 flex items-center justify-between"
          style={{ borderColor: 'hsl(var(--border))' }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                Step {currentStep + 1} of {STEPS.length}
              </span>
              <span className="text-muted">·</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                Miner Inspection Checklist
              </span>
            </div>
            <h1 className="text-xl font-bold text-primary">
              {currentStep === 0 && 'Vessel & Inspection Details'}
              {isCategoryStep && currentCat && `${currentCat.code} — ${currentCat.label}`}
              {isReviewStep && 'Review & Submit Inspection'}
            </h1>
          </div>
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <div className="hidden md:block w-48">
              <div className="flex justify-between text-xs text-muted mb-1">
                <span>Progress</span>
                <span>{Math.round((currentStep / (STEPS.length - 1)) * 100)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(currentStep / (STEPS.length - 1)) * 100}%`,
                    background: 'hsl(var(--primary))',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 px-6 py-6 max-w-screen-xl mx-auto w-full">
          <div className="animate-fade-in">
            {currentStep === 0 && (
              <VesselDetailsStep form={form} />
            )}
            {isCategoryStep && currentCat && (
              <CategoryChecklistStep
                key={`cat-${currentCat.code}`}
                category={currentCat}
                answers={categoryAnswers[currentCat.code] || {}}
                onAnswersChange={(a) => handleCategoryAnswersUpdate(currentCat.code, a)}
                inspectionType={form.watch('inspectionType')}
              />
            )}
            {isReviewStep && (
              <ReviewSubmitStep
                formData={form.getValues()}
                categoryAnswers={categoryAnswers}
                categories={INSPECTION_CATEGORIES}
                getCategoryCompletion={getCategoryCompletion}
              />
            )}
          </div>
        </div>

        {/* Sticky Footer Nav */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between z-30"
          style={{ borderColor: 'hsl(var(--border))' }}>
          <button
            className="btn-secondary"
            onClick={handleBack}
            disabled={currentStep === 0}
            style={{ opacity: currentStep === 0 ? 0.4 : 1 }}
          >
            ← Back
          </button>
          <div className="flex items-center gap-2">
            {isCategoryStep && currentCat && (
              <span className="text-xs text-muted hidden sm:block">
                {getCategoryCompletion(currentCat.code).answered}/{getCategoryCompletion(currentCat.code).total} items answered
              </span>
            )}
            {isReviewStep ? (
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </span>
                ) : 'Submit Inspection'}
              </button>
            ) : (
              <button className="btn-primary" onClick={handleNext}>
                {currentStep === 0 ? 'Begin Inspection →' : 'Next Section →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}